import React, { useState, useEffect } from 'react';
import Map, { Source, Layer } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import sectorsGeoJson from './sectors.geojson';
import tmaGeoJson from './tma.geojson';
import firsJson from './firs.json';
import aerodromesGeoJson from './aerodromes.geojson';
import './styles.css';

const mapboxToken = 'pk.eyJ1Ijoib3R0b3R1aGt1bmVuIiwiYSI6ImNtcTZmaW5qczAwdm8yc3M5a2trazluemsifQ.26_Ibhcm3a2nyUT4CLA4aQ';

const App = () => {
  const [onlineControllers, setOnlineControllers] = useState(['']);
  const [selectedPresetESOS, setSelectedPresetESOS] = useState(null);
  const [selectedPresetESMM, setSelectedPresetESMM] = useState(null); 
  const [displayOption, setDisplayOption] = useState('ALL');
  const [showAccLabels, setShowAccLabels] = useState(true);
  const [showAerodromes, setShowAerodromes] = useState(false);
  const [altitude, setAltitude] = useState(340);
  const [config, setConfig] = useState(null);
  const [filterByAltitude, setFilterByAltitude] = useState(false);


  useEffect(() => {
    fetch('https://ottotuhkunen.github.io/esaa-sector-map-configuration/config.json')
      .then(response => response.json())
      .then(data => setConfig(data))
      .catch(error => console.error('Error fetching config:', error));
  }, []);

  if (!config) {
    return <div>Loading...</div>;
  }

  const { controllerList, sectorsOwnership, connectGroupWithRealSectors } = config;

  const getSectorOwner = (sectorCode, onlineControllers) => {
    const sectorControllers = sectorsOwnership[sectorCode];
    for (let controller of sectorControllers) {
      if (onlineControllers.includes(controller)) {
        return controller;
      }
    }
    return null;
  };

  const toggleController = (callsign) => {
    setSelectedPresetESOS(null);
    setSelectedPresetESMM(null);
    setOnlineControllers(prev =>
      prev.includes(callsign)
        ? prev.filter(c => c !== callsign)
        : [...prev, callsign]
    );
  };

  const applyPreset = (presetControllers, presetName, isESOS) => {
    if (isESOS) {
      setSelectedPresetESOS(prev => (prev === presetName ? null : presetName));
      
      setOnlineControllers(prev => {
        const updatedControllers = prev.filter(controller => !controller.startsWith('OS') && controller !== 'ESAA');
        
        // If deselecting, return only the filtered list
        return selectedPresetESOS === presetName ? updatedControllers : [...updatedControllers, ...presetControllers];
      });
    } else {
      setSelectedPresetESMM(prev => (prev === presetName ? null : presetName));
      
      setOnlineControllers(prev => {
        const updatedControllers = prev.filter(controller => !controller.startsWith('MM') && controller !== 'ESAA');
        
        // If deselecting, return only the filtered list
        return selectedPresetESMM === presetName ? updatedControllers : [...updatedControllers, ...presetControllers];
      });
    }
  };

  const getSectorFillColor = (sectorCode) => {
    const owner = getSectorOwner(sectorCode, onlineControllers);
    const controller = controllerList.find(c => c.name === owner);
    return owner ? controller.color : 'rgba(0, 0, 255, 0)';
  };

  return (
    <div style={{ height: '100vh', backgroundColor: '#1e1e1e', color: '#ffffff' }}>
      <p className={'esos-or-esmm-title'}>ESOS Positions</p>
      <p className={'esos-or-esmm-title'} style={{left: '50%'}}>ESMM Positions</p>

      {/* Controller buttons */}
      <div className={'controller-buttons-container'}>
        {/* Left section for ESAA and ESOS */}
        <div className={'left-container-controllers'}>
          {controllerList
            .filter(controller => controller.name.startsWith('ESAA') || controller.name.startsWith('OS'))
            .map((controller) => (
              <button
                key={controller.name}
                style={{
                  margin: '2px',
                  padding: '6px 0px',
                  background: onlineControllers.includes(controller.name) ? controller.color : '#2b2d31',
                  color: onlineControllers.includes(controller.name) ? 'black' : '#9e9e9e',
                  fontWeight: onlineControllers.includes(controller.name) ? 'bold' : 'normal',
                  border: 'none',
                  cursor: 'pointer',
                  width: '46px',
                  fontSize: '9pt'
                }}
                onClick={() => toggleController(controller.name)}
              >
                {controller.name}
              </button>
            ))}
        </div>

        {/* Right section for ESMM and ESMS */}
        <div style={{ width: '50%', display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-start' }}>
          {controllerList
            .filter(controller => controller.name.startsWith('MM') || controller.name.startsWith('ESMS'))
            .map((controller) => (
              <button
                key={controller.name}
                style={{
                  margin: '2px',
                  padding: '6px 0px',
                  background: onlineControllers.includes(controller.name) ? controller.color : '#2b2d31',
                  color: onlineControllers.includes(controller.name) ? 'black' : '#9e9e9e',
                  fontWeight: onlineControllers.includes(controller.name) ? 'bold' : 'normal',
                  border: 'none',
                  cursor: 'pointer',
                  width: '46px',
                  fontSize: '9pt'
                }}
                onClick={() => toggleController(controller.name)}
              >
                {controller.name}
              </button>
            ))}
        </div>
      </div>


      {/* Map and sectors */}
      <Map
        initialViewState={{
          longitude: 18,
          latitude: 61,
          zoom: 3.9
        }}
        style={{ position: 'fixed', width: '100%', top: '0', bottom: '0', zIndex: '0' }}
        mapStyle="mapbox://styles/ottotuhkunen/cm8g2jfyj00yv01sa0tvx4vof"
        mapboxAccessToken={mapboxToken}
      >
        {/* TMA geojson */}
        <Source type="geojson" data={tmaGeoJson}>
          <Layer
            id="tma-lines"
            type="line"
            paint={{
              'line-color': '#00bfff',
              'line-width': 0.8,
              'line-dasharray': [2, 2],
              'line-opacity': 0.5
            }}
          />
          <Layer
            id="tma-labels"
            type="symbol"
            layout={{
              'text-field': ['get', 'NAMEOFAREA'],
              'text-font': ['Open Sans Regular'],
              'text-size': 10,
            }}
            paint={{
              'text-color': '#1a475f',
              'text-halo-color': 'white',
              'text-halo-width': 0.4
            }}
            minzoom={6}
          />
        </Source>

        {/* FIRs geojson */}
        <Source type="geojson" data={firsJson}>
          <Layer
            id="firs-lines"
            type="line"
            paint={{
              'line-color': '#006400',
              'line-width': 1.5
            }}
          />
        </Source>

        <Source type="geojson" data={sectorsGeoJson}>
          <Layer
            id="sectors-fill"
            type="fill"
            filter={[
              'all',
              displayOption === 'ALL'
                ? ['in', ['get', 'VERTICALFILTER'], ['literal', ['UPPER', 'LOWER']]]
                : ['==', ['get', 'VERTICALFILTER'], displayOption],
              ...(filterByAltitude ? [['>=', altitude, ['get', 'LOWER']], ['<=', altitude, ['get', 'UPPER']]] : [])
            ]}
            paint={{
              'fill-color': [
                'match',
                ['get', 'NAMEOFAREA'],
                ...Object.entries(connectGroupWithRealSectors).flatMap(([key, values]) =>
                  values.flatMap(value => [value, getSectorFillColor(key)])
                ),
                'rgb(255, 255, 255)' // Default color
              ],
              'fill-opacity': 0.4
            }}
          />
          <Layer
            id="sectors-border"
            type="line"
            filter={[
              'all',
              displayOption === 'ALL'
                ? ['in', ['get', 'VERTICALFILTER'], ['literal', ['UPPER', 'LOWER']]]
                : ['==', ['get', 'VERTICALFILTER'], displayOption],
              ['>=', altitude, ['get', 'LOWER']],
              ['<=', altitude, ['get', 'UPPER']]
            ]}
            paint={{
              'line-color': 'gray',
              'line-width': 1
            }}
          />
          {showAccLabels && (
            <Layer
              id="acc-labels"
              type="symbol"
              filter={[
                'all',
                displayOption === 'ALL'
                  ? ['in', ['get', 'VERTICALFILTER'], ['literal', ['UPPER', 'LOWER']]]
                  : ['==', ['get', 'VERTICALFILTER'], displayOption],
                ...(filterByAltitude ? [['>=', altitude, ['get', 'LOWER']], ['<=', altitude, ['get', 'UPPER']]] : [])
              ]}
              layout={{
                'text-field': ['concat', ['get', 'NAMEOFAREA'], '\n', ['get', 'LOWER'], ' - ', ['get', 'UPPER']],
                'text-font': ['Open Sans Bold'],
                'text-size': 10,
                'text-anchor': 'center',
              }}
              paint={{
                'text-color': [
                  'match',
                  ['get', 'NAMEOFAREA'],
                  ...Object.entries(connectGroupWithRealSectors).flatMap(([key, values]) =>
                    values.flatMap(value => [value, getSectorFillColor(key)])
                  ),
                  'rgb(0, 0, 0)' // Default
                ],
              }}
              minzoom={4}
            />
          )}
        </Source>

        {/* Aerodromes layer */}
        {showAerodromes && (
          <Source type="geojson" data={aerodromesGeoJson}>
            <Layer
              id="aerodromes"
              type="symbol"
              layout={{
                'text-field': ['concat', '+ ', ['get', 'icao']],
                'text-font': ['Open Sans Regular'],
                'text-size': 9,
                'text-anchor': 'left',
                'text-offset': [0, 0] 
              }}
              paint={{
                'text-color': '#666666',
                'text-halo-color': 'white',
                'text-halo-width': 0
              }}
            />
          </Source>
        )}

        {/* Altitude slider */}
        <div style={{
          position: 'absolute',
          top: '30%',
          right: '0',
          zIndex: 1,
          userSelect: 'none',
          backgroundColor: '#dddddd',
          padding: '10px',
          width: '90px',
        }}>

          <label style={{ color: 'black', fontSize: '9pt', display: 'flex'}}>
            <input
              type="checkbox"
              checked={showAccLabels}
              onChange={() => setShowAccLabels(!showAccLabels)}
              style={{ marginRight: '5px' }}
            />
            Sector Text
          </label>
          <label style={{ color: 'black', fontSize: '9pt', display: 'flex'}}>
            <input
              type="checkbox"
              checked={showAerodromes}
              onChange={() => setShowAerodromes(!showAerodromes)}
              style={{ marginRight: '5px' }}
            />
            Aerodromes
          </label>
          <br />
          <label style={{ color: 'black', fontSize: '9pt', display: 'flex'}}>
            <input
              type="checkbox"
              checked={filterByAltitude}
              onChange={() => setFilterByAltitude(!filterByAltitude)}
              style={{ marginRight: '5px' }}
            />
            Filter by FL
          </label>

          <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'row', height: '200px' }}>
            <input
              type="range"
              min="0"
              max="660"
              step="10"
              value={altitude}
              onChange={(e) => setAltitude(Number(e.target.value))}
              style={{ 
                writingMode: 'vertical-rl',  
                WebkitAppearance: 'slider-vertical', 
                height: '180px',
                width: '20px',
                transform: 'rotate(180deg)'
              }}
              disabled={!filterByAltitude}
            />
            <div style={{ 
              marginLeft: '10px', 
              color: 'black', 
              fontSize: '10pt', 
              textAlign: 'left',
              color: filterByAltitude ? 'black' : 'gray', 
            }}>
              FL {altitude}
            </div>
          </div>

        </div>

      </Map>
      
      <div>

      <div className={'preset-buttons-container'}>

        {/* Container for ESOS Splits */}
        <div className={'preset-section'}>
          <p className={'esos-or-esmm-title-presets'}>ESOS Splits</p>
          <div className={'left-container'}>
            {config.presetsESOS.map((preset, index) => (
              <button
                key={index}
                style={{
                  margin: '2px',
                  padding: '6px',
                  background: selectedPresetESOS === preset.name ? '#dddddd' : '#2b2d31',
                  color: selectedPresetESOS === preset.name ? 'black' : '#9e9e9e',
                  fontWeight: selectedPresetESOS === preset.name ? 'bold' : 'normal',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '9pt'
                }}
                onClick={() => applyPreset(preset.controllers, preset.name, true)}
              >
                {preset.name}
              </button>
            ))}
          </div>
        </div>

        {/* Container for ESMM Splits */}
        <div className={'preset-section'}>
          <p className={'esos-or-esmm-title-presets'}>ESMM Splits</p>
          <div className={'right-container'}>
            {config.presetsESMM.map((preset, index) => (
              <button
                key={index}
                style={{
                  margin: '2px',
                  padding: '6px',
                  background: selectedPresetESMM === preset.name ? '#dddddd' : '#2b2d31',
                  color: selectedPresetESMM === preset.name ? 'black' : '#9e9e9e',
                  fontWeight: selectedPresetESMM === preset.name ? 'bold' : 'normal',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '9pt'
                }}
                onClick={() => applyPreset(preset.controllers, preset.name, false)}
              >
                {preset.name}
              </button>
            ))}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default App;